import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { normalizeImageUrl } from "@/lib/image-url";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { Briefcase, MapPin, MessageCircle, Share2 } from "lucide-react";
import { Link } from "wouter";

export interface JobItem {
  id: string;
  title: string;
  description?: string;
  priceCents?: number | null; // 시급 (cents)
  images?: string[];
  state?: string;
  city?: string;
  suburb?: string;
  latitude?: number; // 위도 (거리 계산용)
  longitude?: number; // 경도 (거리 계산용)
  // Phase 3.4: 구인/구직 전용 필드
  companyName?: string;
  jobType?: string; // 캐주얼/파트타임, 전문직, 학생알바, 워홀
  workType?: string; // full-time, part-time, casual, contract
  industry?: string; // 업종
  contactMethod?: string;
  phoneNumber?: string;
  kakaoUrl?: string;
  createdAt: string;
  views?: number;
  favorites?: number;
  distance?: number; // 거리 (km)
}

interface JobCardProps {
  item: JobItem;
  onInquire?: () => void;
}

export function JobCard({ item, onInquire }: JobCardProps) {
  const formatWage = () => {
    if (!item.priceCents) return "시급 협의";
    if (item.priceCents === 0) return "무급";
    if (item.priceCents === -1) return "시급 협의";
    return `$${(item.priceCents / 100).toLocaleString()}/시간`;
  };

  const timeAgo = formatDistanceToNow(new Date(item.createdAt), {
    addSuffix: true,
    locale: ko,
  });

  return (
    <Link href={`/jobs/${item.id}`}>
      {/* Phase 3.4: Phase 2 스타일 카드 적용 */}
      <Card className="rounded-xl border-0 shadow-none hover:scale-[1.02] transition-transform overflow-hidden h-full flex flex-col cursor-pointer">
        {/* Image - 16:9 ratio or company logo */}
        <div className="relative aspect-video bg-muted overflow-hidden">
          {item.images && item.images.length > 0 ? (
            <img
              src={normalizeImageUrl(item.images[0]) || item.images[0]}
              alt={item.title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.src =
                  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600"%3E%3Crect fill="%23e5e7eb" width="800" height="600"/%3E%3Ctext fill="%239ca3af" font-family="sans-serif" font-size="32" dy="310" dx="400" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Briefcase className="w-16 h-16 text-muted-foreground/30" />
            </div>
          )}
          {/* Wage overlay */}
          <div className="absolute bottom-2 left-2 bg-background/90 backdrop-blur-sm px-3 py-1 rounded-md">
            <span className="font-bold text-lg">{formatWage()}</span>
          </div>
        </div>

        <div className="flex flex-col flex-1 p-4">
          {/* Title - font-bold, line-clamp-2 */}
          <CardHeader className="space-y-2 pb-3 p-0">
            <h3 className="font-bold line-clamp-2 text-lg leading-tight">
              {item.title}
            </h3>
            {item.companyName && (
              <div className="text-sm text-muted-foreground">
                {item.companyName}
              </div>
            )}
          </CardHeader>

          {/* Meta 정보 */}
          <CardContent className="flex-1 space-y-2 p-0">
            {/* Location, Distance, TimeAgo */}
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
              {item.suburb && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span>
                    {item.suburb}
                    {item.city ? `, ${item.city}` : ""}
                  </span>
                </div>
              )}
              {item.distance !== undefined && (
                <div className="flex items-center gap-1">
                  <span>{item.distance}km</span>
                </div>
              )}
              <div className="flex items-center gap-1">
                <span>{timeAgo}</span>
              </div>
            </div>

            {/* Phase 3.4: 구인/구직 전용 메타 - 업종, 근무형태, 거리 */}
            <div className="flex items-center gap-2 flex-wrap">
              {item.jobType && (
                <Badge variant="outline" className="text-xs">
                  {item.jobType === "casual"
                    ? "캐주얼"
                    : item.jobType === "part-time"
                    ? "파트타임"
                    : item.jobType === "professional"
                    ? "전문직"
                    : item.jobType === "student"
                    ? "학생알바"
                    : item.jobType === "working-holiday"
                    ? "워홀"
                    : item.jobType}
                </Badge>
              )}
              {item.workType && (
                <Badge variant="outline" className="text-xs">
                  {item.workType === "full-time"
                    ? "정규직"
                    : item.workType === "part-time"
                    ? "파트타임"
                    : item.workType === "casual"
                    ? "캐주얼"
                    : item.workType === "contract"
                    ? "계약직"
                    : item.workType}
                </Badge>
              )}
              {item.industry && (
                <Badge variant="outline" className="text-xs">
                  {item.industry}
                </Badge>
              )}
            </div>

            {/* Description */}
            {item.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-2">
                {item.description}
              </p>
            )}
          </CardContent>

          {/* Footer - CTA: "문의하기" */}
          <CardFooter className="flex items-center justify-between text-sm text-muted-foreground pt-2 p-0 border-t border-line-subtle mt-2">
            <div className="flex items-center gap-3">
              {item.views !== undefined &&
                item.views !== null &&
                item.views > 0 && (
                  <div className="flex items-center gap-1">
                    <span>{item.views.toLocaleString()} views</span>
                  </div>
                )}
            </div>
            <div className="flex items-center gap-2">
              {/* Phase 3.4: 문의하기 버튼 */}
              <Button
                size="sm"
                className="bg-brand-orange hover:bg-brand-orange/90"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (onInquire) {
                    onInquire();
                  } else if (item.kakaoUrl) {
                    window.open(item.kakaoUrl, "_blank");
                  } else {
                    // TODO: 내장 채팅 시작
                    window.location.href = `/chat-start/jobs/${item.id}`;
                  }
                }}
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                문의하기
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  // TODO: 공유 로직
                }}
              >
                <Share2 className="h-4 w-4" />
              </Button>
            </div>
          </CardFooter>
        </div>
      </Card>
    </Link>
  );
}
